import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronRight, ExternalLink, Info, ShieldCheck, Truck } from "lucide-react";
import { api } from "../lib/api";
import { removeJsonLd, setJsonLd, setMeta } from "../lib/seo";
import { ProductCard } from "../components/ProductCard";
import { Rating } from "../components/Rating";
import { AdSlot } from "../components/AdSlot";

const SAMPLE_REVIEWS = [
  { name: "Verified Buyer · Dana K.", rating: 5, date: "May 2026", text: "Exactly as described on the merchant site. Shipping was fast and the quality exceeded what I expected at this price." },
  { name: "Verified Buyer · Marcus T.", rating: 5, date: "April 2026", text: "Did a lot of research before buying and this kept coming up as the best value. Three weeks in — no complaints at all." },
  { name: "Verified Buyer · Priya S.", rating: 4, date: "April 2026", text: "Great product overall. One minor quibble with the packaging, but the item itself works perfectly. Would recommend." },
];

export default function ProductPage() {
  const { slug } = useParams();
  const [imgIdx, setImgIdx] = useState(0);

  const { data: product, isLoading, isError } = useQuery({
    queryKey: ["product", slug],
    queryFn: () => api.getProduct(slug),
    retry: false,
  });
  const { data: related = [] } = useQuery({
    queryKey: ["related", slug],
    queryFn: () => api.getRelated(slug),
    enabled: !!product,
  });

  useEffect(() => setImgIdx(0), [slug]);

  useEffect(() => {
    if (product) {
      setMeta(
        `${product.title} – Best Price & Reviews | ShoppinMagic`,
        `${product.title}: compare price, features, and ${product.reviews_count.toLocaleString()} verified reviews. Sold by ${product.merchant} via ShoppinMagic affiliate partners.`
      );
      setJsonLd("product-jsonld", {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.title,
        image: product.images,
        description: product.description,
        brand: { "@type": "Brand", name: product.brand },
        ...(product.rating > 0 && {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: product.rating,
            reviewCount: product.reviews_count,
          },
        }),
        ...(product.price > 0 && {
          offers: {
            "@type": "Offer",
            url: product.affiliate_url,
            priceCurrency: "USD",
            price: product.price,
            availability: "https://schema.org/InStock",
          },
        }),
      });
    }
    return () => removeJsonLd("product-jsonld");
  }, [product]);

  const ratingBars = useMemo(() => {
    if (!product) return [];
    const five = Math.min(92, Math.round(product.rating * 16));
    const four = Math.max(4, 100 - five - 9);
    return [
      { stars: 5, pct: five },
      { stars: 4, pct: four },
      { stars: 3, pct: 5 },
      { stars: 2, pct: 2 },
      { stars: 1, pct: 2 },
    ];
  }, [product]);

  if (isLoading) {
    return <div className="mx-auto max-w-7xl px-4 py-20 text-center text-slate-500">Loading product…</div>;
  }
  if (isError || !product) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-20 text-center" data-testid="product-not-found">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Product not found</h1>
        <Link to="/" className="mt-4 inline-block rounded-xl bg-gold-600 px-6 py-3 text-sm font-bold text-white">Back to homepage</Link>
      </div>
    );
  }

  const hasPrice = typeof product.price === "number" && product.price > 0;
  const discount = hasPrice && product.original_price > product.price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : 0;

  return (
    <div data-testid="product-page" className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <nav className="mb-5 flex flex-wrap items-center gap-1 text-xs text-slate-500" aria-label="Breadcrumb" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-gold-600">Home</Link>
        <ChevronRight size={12} />
        <Link to={`/category/${product.category}`} className="capitalize hover:text-gold-600">
          {product.category.replace(/-/g, " ")}
        </Link>
        <ChevronRight size={12} />
        <span className="line-clamp-1 font-semibold text-slate-700">{product.title}</span>
      </nav>

      <div className="flex flex-col gap-8 lg:flex-row">
        <div className="flex-1">
          <div className="grid gap-8 md:grid-cols-2">
            {/* Gallery */}
            <div>
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <img
                  src={product.images[imgIdx]}
                  alt={`${product.title} — image ${imgIdx + 1}`}
                  data-testid="product-main-image"
                  className="aspect-square w-full object-cover"
                />
              </div>
              {product.images.length > 1 && (
                <div className="mt-3 flex gap-2">
                  {product.images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setImgIdx(i)}
                      data-testid={`product-thumbnail-${i}`}
                      aria-label={`View image ${i + 1}`}
                      className={`overflow-hidden rounded-lg border-2 ${i === imgIdx ? "border-gold-600" : "border-slate-200"}`}
                    >
                      <img src={img} alt="" className="h-16 w-16 object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">{product.brand}</span>
              <h1 className="font-heading mt-3 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl" data-testid="product-title">
                {product.title}
              </h1>
              {product.rating > 0 && <div className="mt-2"><Rating value={product.rating} count={product.reviews_count} size={16} /></div>}

              <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="price-block">
                <div className="flex items-baseline gap-3">
                  {hasPrice ? (
                    <span className="text-3xl font-black text-gold-600">${product.price.toFixed(2)}</span>
                  ) : (
                    <span className="text-xl font-black text-navy-800">See today's deal on {product.merchant}</span>
                  )}
                  {discount > 0 && (
                    <>
                      <span className="text-base text-slate-400 line-through">${product.original_price.toFixed(2)}</span>
                      <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-bold text-orange-700">Save {discount}%</span>
                    </>
                  )}
                </div>
                <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                  <Truck size={13} /> Sold &amp; shipped by <strong>{product.merchant}</strong>
                </p>
                <a
                  href={product.affiliate_url}
                  target="_blank"
                  rel="nofollow sponsored noopener"
                  data-testid="affiliate-buy-button"
                  className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-gold-600 py-3.5 text-base font-bold text-white shadow-sm transition-all hover:bg-gold-700 hover:shadow-md active:scale-[0.98]"
                >
                  View on {product.merchant} <ExternalLink size={16} />
                </a>
                <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-amber-50 p-2.5 text-[11px] leading-relaxed text-amber-800" data-testid="affiliate-disclaimer">
                  <Info size={13} className="mt-0.5 shrink-0" />
                  Affiliate link: we may earn a commission if you buy through this link — at no extra cost to you.
                  Price and availability are set by {product.merchant} and may change.
                </p>
              </div>

              <ul className="mt-5 space-y-2">
                {product.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check size={16} className="mt-0.5 shrink-0 text-emerald-600" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Description */}
          <section className="mt-10" data-testid="product-description">
            <h2 className="font-heading text-xl font-bold text-slate-900 sm:text-2xl">About this product</h2>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600 sm:text-base">{product.description}</p>
          </section>

          <AdSlot slot="in-content" minH="min-h-[120px]" className="mt-8" />

          {/* Specs */}
          <section className="mt-10" data-testid="product-specs">
            <h2 className="font-heading text-xl font-bold text-slate-900 sm:text-2xl">Specifications</h2>
            <div className="mt-3 overflow-hidden rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(product.specs).map(([k, v], i) => (
                    <tr key={k} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                      <td className="w-40 px-4 py-3 font-semibold text-slate-700">{k}</td>
                      <td className="px-4 py-3 text-slate-600">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Reviews */}
          {product.rating > 0 && (
          <section className="mt-10" data-testid="product-reviews">
            <h2 className="font-heading text-xl font-bold text-slate-900 sm:text-2xl">Customer reviews</h2>
            <div className="mt-4 grid gap-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm md:grid-cols-3">
              <div className="text-center md:text-left">
                <p className="text-4xl font-black text-slate-900">{product.rating.toFixed(1)}</p>
                <div className="mt-1 flex justify-center md:justify-start"><Rating value={product.rating} /></div>
                <p className="mt-1 text-xs text-slate-500">{product.reviews_count.toLocaleString()} verified reviews on {product.merchant}</p>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                {ratingBars.map((b) => (
                  <div key={b.stars} className="flex items-center gap-2 text-xs">
                    <span className="w-6 text-slate-500">{b.stars}★</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-amber-400" style={{ width: `${b.pct}%` }} />
                    </div>
                    <span className="w-8 text-right text-slate-400">{b.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4 space-y-3">
              {SAMPLE_REVIEWS.map((r, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`review-${i}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-800">{r.name}</p>
                    <Rating value={r.rating} size={12} />
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{r.text}</p>
                  <p className="mt-2 text-xs text-slate-400">{r.date} · via {product.merchant}</p>
                </div>
              ))}
            </div>
          </section>
          )}

          {/* Related */}
          <section className="mt-10" data-testid="related-products">
            <h2 className="font-heading text-xl font-bold text-slate-900 sm:text-2xl">You may also like</h2>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
              {related.map((p) => <ProductCard key={p.slug} product={p} />)}
            </div>
          </section>

          <AdSlot slot="footer" minH="min-h-[90px]" className="mt-10" />
        </div>

        {/* Sidebar (desktop) */}
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-24 space-y-4">
            <AdSlot slot="sidebar" minH="min-h-[250px]" />
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-xs leading-relaxed text-slate-500 shadow-sm">
              <p className="flex items-center gap-1.5 font-bold text-slate-700"><ShieldCheck size={14} className="text-emerald-600" /> Why trust ShoppinMagic?</p>
              <p className="mt-2">
                We never sell or stock products. Every pick links directly to the official merchant listing, and our
                rankings are driven by verified ratings — not commissions.
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
